// [NOTE]
// Branded types are used to statically differentiate between two or more values
// which have the same TypeScript type, but should not be confused for eachother.
//
// For example:
//
// ```ts
// function canJoin(playerId: string, worldId: string) { ... }
// ```
//
// In the above code snippet, TypeScript will not error if `canJoin` is called
// with the `playerId` and `worldId` in the reverse order.
//
// To fix this, we can use branded types. These include additional information
// in the type signature, which is not present at runtime.
//
// For example:
//
// ```ts
// const PlayerId = createBrandedType<string, "playerId">();
// const WorldId = createBrandedType<string, "worldId">();
//
// function canJoin(
//   playerId: typeof PlayerId.Type,
//   worldId: typeof WorldId.Type,
// ) { ... }
// ```
//
// This can then be called safely elsewhere in the code:
//
// ```ts
// canJoin(PlayerId(playerId), WorldId(worldId));
// ```
//
// Note that nothing stops us from incorrectly branding our primitive types:
//
// ```ts
// canJoin(WorldId(playerId), PlayerId(worldId));
// ```
//
// However, this mistake is much easier to spot in code review, as it is
// contained to a single line in a single file. Furthermore, once branded, these
// values can be passed around our codebase safely, without risk of confusion.

export type Branded<T, M> = T & { __brand: M | undefined };

export type MakeFn<T, M> = ((value: T) => Branded<T, M>) & {
  Type: Branded<T, M>;
};

export function createBrandedType<T, M>(): MakeFn<T, M> {
  function make(value: T): Branded<T, M> {
    return value as Branded<T, M>;
  }

  return make as MakeFn<T, M>;
}
